'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/auth-context';
import { getSocket } from '@/lib/echo';

import { ocrKeys } from '../services/ocr.queries';
import type { ActiveOcrJob, OcrJobLifecyclePayload } from '../types';

interface UseOcrUserChannelOptions {
  onCompleted?: (event: OcrJobLifecyclePayload) => void;
  onFailed?: (event: OcrJobLifecyclePayload) => void;
}

/**
 * Subscribe to the user-scoped lifecycle channel so the global active-jobs
 * widget reacts to start/finish/fail transitions without polling.
 *
 * Patches the active-jobs query cache in place rather than re-fetching so
 * the badge updates instantly when an event arrives.
 */
export function useOcrUserChannel({
  onCompleted,
  onFailed
}: UseOcrUserChannelOptions = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    getSocket().then((socket) => {
      if (cancelled) return;
      const channel = socket.private(`ocr-user.${userId}`);

      channel.listen('.ocr-job-lifecycle', (event: OcrJobLifecyclePayload) => {
        queryClient.setQueryData<{ data: ActiveOcrJob[] }>(
          ocrKeys.activeJobs(),
          (old) => {
            const list = old?.data ?? [];
            const isInFlight =
              event.status === 'pending' || event.status === 'processing';

            if (isInFlight) {
              const total = event.total_pages ?? 0;
              const current = event.current_page ?? 0;
              const patch: ActiveOcrJob = {
                id: event.ocr_result_id,
                status: event.status as ActiveOcrJob['status'],
                progress_stage: event.progress_stage,
                current_page: current,
                total_pages: total,
                progress_percent:
                  total > 0
                    ? Math.min(100, Math.round((current / total) * 100))
                    : 0,
                container_id: event.container_id,
                container_label: event.container_label,
                error_code: event.error_code,
                started_at: new Date().toISOString()
              };

              const existing = list.find((j) => j.id === event.ocr_result_id);
              const next = existing
                ? list.map((j) =>
                    j.id === event.ocr_result_id ? { ...j, ...patch } : j
                  )
                : [patch, ...list];

              return { ...(old ?? { data: [] }), data: next };
            }

            // Completed/failed/skipped: remove from active list.
            return {
              ...(old ?? { data: [] }),
              data: list.filter((j) => j.id !== event.ocr_result_id)
            };
          }
        );

        if (event.status === 'completed') {
          onCompleted?.(event);
        } else if (event.status === 'failed') {
          onFailed?.(event);
        }

        // Also bump container-level queries so the listing badge updates.
        queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
      });
    });

    return () => {
      cancelled = true;
      getSocket().then((socket) => socket.leave(`ocr-user.${userId}`));
    };
  }, [userId, queryClient, onCompleted, onFailed]);
}
